import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Avatar } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Success from '../../iconsV2/Success'
import {
  StockRecord,
  MedicationStockReason,
  DiscrepancyStatus
} from '../../types'

type StockCheckDetailContentProps = {
  record: StockRecord
  medicationName: string
}

export const StockCheckDetailContent = ({
  record
}: StockCheckDetailContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const description = Array.isArray(record.notes)
    ? record.notes.join(', ')
    : record.notes || ''

  const stockCount = record.stockCount ?? record.resultingStockLevel ?? 0

  let expectedStock = record.expectedStock ?? record.previousStockLevel
  if (
    expectedStock === undefined &&
    stockCount !== undefined &&
    record.quantityChange !== undefined
  ) {
    expectedStock = stockCount - record.quantityChange
  }
  expectedStock = expectedStock ?? 0

  // Previous Expected Stock is the expected stock before this record
  const previousExpectedStock = record.previousStockLevel ?? expectedStock

  const discrepancy = record.discrepancy?.amount
  const isDiscrepancyResolved =
    record.discrepancy?.status === DiscrepancyStatus.RECONCILED
  const discrepancyReason =
    record.discrepancy?.reason || record.discrepancy?.carerReason || ''

  const timeLogged = dayjs(record.date).format('DD-MM-YYYY HH:mm')
  const carerName = record.recordedBy.name || ''
  const witnesses = record.witnesses || []

  return (
    <SectionContainer>
      <FormCard level={2}>
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
            <Text>{description || '-'}</Text>
          </FullWidthSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('PREVIOUS_EXPECTED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {previousExpectedStock !== undefined &&
              previousExpectedStock !== null
                ? `${previousExpectedStock} ${record.unit || t('TABLETS')}`
                : '-'}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('STOCK_COUNT')}</StyledLabel>
            <StockCountContainer>
              {stockCount !== undefined && stockCount !== null ? (
                <>
                  <SuccessIconWrapper>
                    <Success />
                  </SuccessIconWrapper>
                  <Text weight="bold">
                    {stockCount} {record.unit || t('TABLETS')}
                  </Text>
                </>
              ) : (
                <Text weight="bold">-</Text>
              )}
            </StockCountContainer>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('DISCREPANCY')}</StyledLabel>
            {record.action === MedicationStockReason.RECONCILIATION ? (
              <DiscrepancyValue $noDiscrepancy weight="bold">
                {`0 ${record.unit || t('TABLETS')}`}
              </DiscrepancyValue>
            ) : discrepancy !== undefined ? (
              <DiscrepancyValue weight="bold">
                {discrepancy > 0 ? '+' : ''}
                {discrepancy} {record.unit || t('TABLETS')}
              </DiscrepancyValue>
            ) : (
              <Text weight="bold">-</Text>
            )}
          </InfoSection>
        </InfoRow>
        {discrepancy !== undefined && (
          <>
            <InfoRow>
              <InfoSection>
                <StyledLabel level={2}>{t('RESOLVE_DISCREPANCY')}?</StyledLabel>
                <Text weight="bold">
                  {isDiscrepancyResolved ? t('YES') : t('NO')}
                </Text>
              </InfoSection>
              {discrepancyReason && (
                <DiscrepancyReasonSection>
                  <StyledLabel level={2}>
                    {t('REASON_FOR_DISCREPANCY')}
                  </StyledLabel>
                  <Text>{discrepancyReason}</Text>
                </DiscrepancyReasonSection>
              )}
            </InfoRow>
          </>
        )}
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECORDED_BY')}</StyledLabel>
            <RecordedByContainer>
              <Avatar
                url={record.recordedBy.picture as unknown as string}
                size={16}
              />
              <Text>{carerName || '-'}</Text>
            </RecordedByContainer>
          </InfoSection>
          {record.witnessed && witnesses.length > 0 && (
            <InfoSection>
              <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
              <Text weight="bold">
                {witnesses.length} {t('WITNESSES')}
              </Text>
            </InfoSection>
          )}
        </InfoRow>
      </FormCard>
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const DiscrepancyReasonSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DiscrepancyValue = styled(Text)<{
  $isPositive?: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledDivider = styled(Divider)`
  margin: 24px 0;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const StockCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const RecordedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
